/**
 * ProfileLogin — avatarrutenett + PIN på en innrullert kjøkkenskjerm
 * (Plan 01-12, skilt ut av LoginRoute i #141, flyttet bak enheten i #142).
 *
 *   1. Avatarrutenett fra GET /api/auth/roster — bare enhetens familie, og
 *      bare på en innrullert enhet (404 ellers, lukker #6).
 *   2. PinKeypad — sender 4 sifre til POST /api/auth/pin. Serveren gir en
 *      'tablet'-økt bundet til enheten og avslutter forrige persons økt der.
 *
 * Et medlem uten PIN (`has_pin: false`) sendes til passordskjemaet med
 * brukernavnet utfylt.
 *
 * Utseendet følger Stell_KjokkenPIN (#160, steg 10b): klokke og «Avbryt»
 * øverst, avatar og «Hei, {navn}!» til venstre, store taster til høyre.
 * Avbryt går tilbake til avatarrutenettet, som «Tilbake» gjorde før.
 * Rutenettet har ingen egen skisse; knappene låner medlemsknappene fra
 * «Hvem er du?» i Stell_Kjokken (MemberButton).
 *
 * #162 (D6): hvilemodus sender hit med et medlem valgt (`initialMemberId`)
 * og `onCancel`, så Avbryt går tilbake til hvile i stedet for til
 * rutenettet. «Ikke {navn}?» bytter rett til et annet medlem uten omveien
 * om rutenettet, og ledeteksten sier «Logger inn …» mens PIN-en sjekkes.
 * Bytte av medlem nullstiller nedtellingen akkurat som Avbryt; serveren
 * teller fortsatt feil per medlem og enhet, og usePinLockout er uendret.
 *
 * Feil:
 *   - roster svarer ikke 200 (enheten er tilbakekalt i mellomtiden) →
 *     onUnavailable(), LoginRoute faller tilbake til bare passord
 *   - 401/429 + Retry-After → usePinLockout.startLockout(n) + feilmelding
 *   - 401 uten Retry-After → t('pin.wrong')
 *   - nettverksfeil → egen melding
 *   - 200 → onSuccess() — LoginRoute eier omdirigeringen
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { LoginResponse, RosterMember } from '@stell/shared';
import { PinKeypad } from '../../components/PinKeypad';
import { InitialAvatar, asColorSlot } from '../../components/primitives/InitialAvatar';
import { MemberButton, memberDisplayName } from './MemberButton';
import { formatTime } from '../../i18n/formatters';
import { useMinuteClock } from '../../components/hooks/useMinuteClock';
import { usePinLockout } from '../../components/hooks/usePinLockout';
import { AuthApi, asApiError, authErrorMessage } from '../auth/authApi';

export interface ProfileLoginProps {
  /** Får svaret, så LoginRoute kan sende til /velg-passord (#143). */
  onSuccess: (res: LoginResponse) => void;
  /**
   * «Logg inn med brukernavn» — LoginRoute bytter til passordskjemaet, med
   * brukernavnet utfylt når det kommer fra et medlem uten PIN.
   */
  onUsePassword: (username?: string) => void;
  /** Rosteret var ikke tilgjengelig — enheten er ikke (lenger) innrullert. */
  onUnavailable: () => void;
  /** #162: medlemmet som ble valgt i hvilemodus; PIN-skjermen åpnes rett på. */
  initialMemberId?: string | undefined;
  /** #162: Avbryt på PIN-skjermen kaller denne (tilbake til hvile) i stedet for å vise rutenettet. */
  onCancel?: (() => void) | undefined;
}

export function ProfileLogin({
  onSuccess,
  onUsePassword,
  onUnavailable,
  initialMemberId,
  onCancel,
}: ProfileLoginProps): ReactElement {
  const { t } = useTranslation();
  const lockout = usePinLockout();
  const now = useMinuteClock();

  // Når låsingen er over, er «Feil PIN» fra siste forsøk gammel nytt; uten
  // dette dukket den opp igjen idet banneret forsvant (#160).
  const locked = lockout.secondsRemaining > 0;
  const wasLocked = useRef(false);
  useEffect(() => {
    if (wasLocked.current && !locked) setErrorMessage(null);
    wasLocked.current = locked;
  }, [locked]);

  const [members, setMembers] = useState<RosterMember[] | null>(null);
  const [selected, setSelected] = useState<RosterMember | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const choose = useCallback(
    (m: RosterMember) => {
      if (!m.has_pin) {
        onUsePassword(m.username ?? undefined);
        return;
      }
      setSelected(m);
    },
    [onUsePassword],
  );

  useEffect(() => {
    let cancelled = false;
    AuthApi.roster().then(
      (body) => {
        if (cancelled) return;
        setMembers(body.members);
        // Valgt i hvilemodus (#162). Finnes medlemmet ikke lenger, vises rutenettet.
        const preset = body.members.find((m) => m.id === initialMemberId);
        if (preset) choose(preset);
      },
      () => {
        if (!cancelled) onUnavailable();
      },
    );
    return () => {
      cancelled = true;
    };
  }, [onUnavailable, initialMemberId, choose]);

  const onSubmitPin = useCallback(
    async (pin: string) => {
      if (!selected) return;
      setErrorMessage(null);
      setSubmitting(true);
      try {
        const res = await AuthApi.pin({ memberId: selected.id, pin });
        lockout.cancel();
        onSuccess(res);
      } catch (err) {
        setSubmitting(false);
        // 401 eller 429 — Retry-After styrer nedtellingen.
        const e = asApiError(err);
        if (e.retryAfter) lockout.startLockout(e.retryAfter);
        setErrorMessage(e.code === 'network' ? authErrorMessage(t, e) : t('pin.wrong'));
      }
    },
    [selected, lockout, onSuccess, t],
  );

  // Felles for Avbryt og «Ikke {navn}?»: glem forrige medlems forsøk.
  function resetAttempt(): void {
    setErrorMessage(null);
    setSubmitting(false);
    lockout.cancel();
  }

  if (selected) {
    const name = memberDisplayName(selected);
    const others = (members ?? []).filter((m) => m.id !== selected.id);
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-10 px-5 pt-6 pb-10 md:gap-16 md:px-10 md:pt-9">
        <header className="flex items-center justify-between">
          <span data-testid="login-clock" className="tnum text-heading-m font-extrabold">
            {formatTime(now)}
          </span>
          <button
            data-testid="login-back"
            type="button"
            // Mens PIN-en sjekkes, kan Avbryt ikke lenger stoppe innloggingen.
            disabled={submitting}
            className="bg-card border-card-line text-ink flex h-13 items-center rounded-pill border px-5.5 text-body font-extrabold"
            onClick={() => {
              resetAttempt();
              if (onCancel) onCancel();
              else setSelected(null);
            }}
          >
            {t('common.cancel')}
          </button>
        </header>
        <div className="md:px-10">
          {/* Nøkkelen nullstiller tastaturet når «Ikke {navn}?» bytter medlem. */}
          <PinKeypad
            key={selected.id}
            captureGlobalKeys
            variant="kitchen"
            length={4}
            onSubmit={(pin) => void onSubmitPin(pin)}
            disabled={submitting}
            errorMessage={errorMessage}
            lockoutSecondsRemaining={lockout.secondsRemaining}
            lockoutMessage={lockout.lockoutMessage}
            lead={
              <>
                <InitialAvatar
                  name={name}
                  colorSlot={asColorSlot(selected.color_token)}
                  size={140}
                  decorative
                  className="ring-8 ring-track"
                />
                <div>
                  <h1 className="m-0 text-4xl font-extrabold tracking-tight md:text-5xl">
                    {t('login.pin_greeting', { name })}
                  </h1>
                  <p
                    data-testid="login-pin-prompt"
                    aria-live="polite"
                    className="text-ink-2 mt-1.5 mb-0 text-xl"
                  >
                    {t(submitting ? 'login.pin_signing_in' : 'login.pin_prompt')}
                  </p>
                </div>
              </>
            }
            trail={
              others.length > 0 ? (
                <div
                  data-testid="login-switch"
                  className="mt-2 flex flex-wrap items-center gap-2.5"
                >
                  <span className="text-ink-2 mr-1.5 text-body font-bold">
                    {t('login.not_member', { name })}
                  </span>
                  {others.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`login-switch-${m.id}`}
                      type="button"
                      aria-label={t('login.member_aria', { name: m.name })}
                      className="rounded-full disabled:opacity-50"
                      // Mens PIN-en sjekkes, kan et bytte ikke lenger angre innloggingen.
                      disabled={submitting}
                      onClick={() => {
                        resetAttempt();
                        choose(m);
                      }}
                    >
                      <InitialAvatar
                        name={memberDisplayName(m)}
                        colorSlot={asColorSlot(m.color_token)}
                        size={56}
                        decorative
                      />
                    </button>
                  ))}
                </div>
              ) : null
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-10 px-5 py-12 md:py-20">
      <h1 className="m-0 text-heading-l font-extrabold">{t('login.choose_member')}</h1>
      {members === null ? (
        <p data-testid="login-loading" className="text-ink-2 text-body">
          {t('login.loading')}
        </p>
      ) : members.length === 0 ? (
        <p data-testid="login-empty" className="text-ink-2 text-body">
          {t('login.members_empty')}
        </p>
      ) : (
        <div
          data-testid="login-profile-grid"
          className="flex flex-wrap justify-center gap-4"
        >
          {members.map((m) => (
            <MemberButton
              key={m.id}
              member={m}
              testId={`login-member-${m.id}`}
              onClick={() => choose(m)}
            />
          ))}
        </div>
      )}
      <button
        data-testid="login-use-password"
        type="button"
        className="text-ink min-h-11 px-3 text-sm font-bold underline"
        onClick={() => onUsePassword()}
      >
        {t('login.password.use_password')}
      </button>
    </div>
  );
}
